import { useEffect, useMemo, type ReactNode } from "react";
import { BanknoteCheck, Gavel, ShoppingCartPlus, Summary, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";

import { listTraders, updateTraderRepresentative } from "../../actions";
import { TRADER_TYPES } from "../../containers/TraderDetails/components/Form/Components/TraderDetailsStepOne/utils";
import { useNavigationLanguage } from "../../hooks/useNavigationLanguage";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import type { NavigationUser } from "../NavigationUserMenu/types";
import {
  SidebarNavItem,
  SidebarToggle,
  SidebarUserMenu,
  sidebarStyle,
  type SidebarNavigationItem,
} from "../SidebarNavigation";

type Organization = {
  id?: number | string;
};

type TraderRepresentative = {
  id?: number | string;
  language?: string;
  organization?: Organization | number | string;
  [key: string]: unknown;
};

type TraderOrganization = {
  trader_type?: string;
};

type RootState = {
  traderRepresentativeDetails?: {
    data?: TraderRepresentative;
  };
  listTraders?: {
    data?: { results?: TraderOrganization[] };
  };
  userDetail?: {
    data?: NavigationUser;
  };
};

type RepresentativeMainNavigationProps = {
  children: ReactNode;
};

type RepresentativeSidebarProps = {
  items: SidebarNavigationItem[];
  user?: NavigationUser;
};

const homePath = "/representative/";

const getNavigationItems = (isSchool: boolean): SidebarNavigationItem[] => [
  { labelKey: "header.overview", to: homePath, icon: Summary, exact: true },
  { labelKey: "header.forSale", to: "/representative/market", icon: Tag },
  { labelKey: "header.offerView", to: "/representative/offer", icon: BanknoteCheck },
  {
    labelKey: "header.newTraderDemand",
    to: "/representative/marketOfferDetails",
    icon: ShoppingCartPlus,
  },
  ...(isSchool
    ? [{ labelKey: "header.bidRequests", to: "/representative/bidRequests", icon: Gavel }]
    : []),
];

const getOrganizationId = (organization: TraderRepresentative["organization"]) => {
  if (organization && typeof organization === "object") {
    return organization.id;
  }

  return organization;
};

const Logo = () => {
  const { t } = useTranslation();

  return (
    <Link to={homePath} className="flex items-center gap-1.5">
      <img src="/static/logos/farm2go.svg" alt={t("header.farm2go")} className="size-6" />
      <p className="font-bold text-base group-data-[collapsible=icon]:hidden">
        {t("header.farm2go")}
      </p>
    </Link>
  );
};

const RepresentativeSidebar = ({ items, user }: RepresentativeSidebarProps) => {
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();

  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  return (
    <Sidebar collapsible="icon" className="z-40">
      <SidebarHeader className="px-4 py-5 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-2">
        <Logo />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu className="group-data-[collapsible=icon]:items-center">
            {items.map(item => (
              <SidebarNavItem key={item.to} item={item} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="gap-3 pb-4">
        <SidebarSeparator className="mx-2" />
        <SidebarUserMenu user={user} profilePath="/representative/profile" />
      </SidebarFooter>
      {!isMobile && <SidebarToggle />}
    </Sidebar>
  );
};

export const RepresentativeMainNavigation = ({ children }: RepresentativeMainNavigationProps) => {
  const dispatch = useDispatch<Dispatch<UnknownAction>>();
  const { selectedLanguage } = useNavigationLanguage();
  const traderRepresentative = useSelector(
    (state: RootState) => state.traderRepresentativeDetails?.data
  );
  const trader = useSelector((state: RootState) => state.listTraders?.data?.results?.[0]);
  const user = useSelector((state: RootState) => state.userDetail?.data);

  const isSchool = trader?.trader_type === TRADER_TYPES.school;
  const items = useMemo(() => getNavigationItems(isSchool), [isSchool]);

  useEffect(() => {
    dispatch(listTraders() as unknown as UnknownAction);
  }, [dispatch]);

  useEffect(() => {
    const organizationId = getOrganizationId(traderRepresentative?.organization);

    if (
      !selectedLanguage ||
      !traderRepresentative?.id ||
      !traderRepresentative.language ||
      !organizationId ||
      traderRepresentative.language === selectedLanguage
    ) {
      return;
    }

    dispatch(
      updateTraderRepresentative(traderRepresentative.id, {
        ...traderRepresentative,
        organization: organizationId,
        language: selectedLanguage,
      }) as unknown as UnknownAction
    );
  }, [dispatch, selectedLanguage, traderRepresentative]);

  return (
    <SidebarProvider style={sidebarStyle}>
      <RepresentativeSidebar items={items} user={user} />
      <SidebarInset className="min-w-0">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-background px-4 md:hidden">
          <SidebarTrigger />
          <Logo />
        </header>
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
};
